import React, { useState, useRef, useEffect } from 'react';
import { BankInstitution } from '../../types';

interface BankSelectionProps {
  label?: string;
  banks: BankInstitution[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  loading?: boolean;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

const BankSelection: React.FC<BankSelectionProps> = ({
  label,
  banks,
  value,
  onChange,
  error,
  loading = false,
  placeholder = 'Meklējiet vai izvēlieties banku',
  required = false,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const selectedBank = banks.find(bank => bank.id === value);
  
  // Filter banks based on search term
  const filteredBanks = banks.filter(bank =>
    bank.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputFocus = () => {
    if (!disabled && !loading) {
      setIsOpen(true);
      if (selectedBank && !searchTerm) {
        setSearchTerm('');
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    if (e.target.value && selectedBank) {
      onChange('');
    }
  };

  const handleBankSelect = (bank: BankInstitution) => {
    onChange(bank.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={`space-y-2 ${label ? 'mt-6' : ''} ${className}`}>
      {label && (
        <label className="block text-base font-semibold text-gray-700 mb-0.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative" ref={selectRef}>
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm || (selectedBank ? selectedBank.name : '')}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled || loading}
            className={`
              w-full bg-white border border-gray-300 rounded-10 pl-10 pr-10 h-10 text-base
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-colors duration-200 flex items-center
              ${error ? 'border-red-500 bg-red-50' : 'hover:border-gray-400'}
              ${disabled || loading ? 'bg-gray-100 cursor-not-allowed' : 'cursor-text'}
            `}
          />
          
          {/* Bank logo or search icon */}
          <div className="absolute inset-y-0 left-0 flex items-center pl-3">
            {selectedBank && !searchTerm ? (
              <img
                src={selectedBank.logo}
                alt={selectedBank.name}
                className="h-5 w-5 object-contain"
                onError={(e) => {
                  // Fallback to generic bank icon if logo fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </div>
          
          {/* Dropdown arrow */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
            ) : (
              <svg className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>
        
        {isOpen && !loading && !disabled && (
          <div className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-60 rounded-10 py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none border border-gray-200">
            {filteredBanks.length > 0 ? (
              <ul className="py-1" tabIndex={-1} role="listbox">
                {filteredBanks.map((bank) => (
                  <li
                    key={bank.id}
                    className={`
                      cursor-pointer select-none relative py-3 pl-3 pr-9 flex items-center space-x-3 text-left
                      ${bank.id === value ? 'text-white bg-primary-500' : 'text-gray-900 hover:bg-gray-100'}
                    `}
                    onClick={() => handleBankSelect(bank)}
                    role="option"
                    aria-selected={bank.id === value}
                  >
                    <img
                      src={bank.logo}
                      alt={bank.name}
                      className="h-6 w-6 object-contain flex-shrink-0"
                      onError={(e) => {
                        // Hide image if it fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                    <div className="flex-1">
                      <span className={`block font-medium truncate text-left ${bank.id === value ? 'font-medium' : 'font-normal'}`}>
                        {bank.name}
                      </span>
                      {bank.bic && (
                        <span className={`block text-xs truncate text-left ${bank.id === value ? 'text-gray-200' : 'text-gray-500'}`}>
                          BIC: {bank.bic}
                        </span>
                      )}
                    </div>
                    
                    {bank.id === value && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="py-3 px-3 text-gray-500 text-center text-left">
                {searchTerm ? `Nav atrasta neviena banka "${searchTerm}"` : 'Nav pieejamas bankas'}
              </div>
            )}
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};

export default BankSelection;
